import { useRef } from 'react';

import { Check } from '@phosphor-icons/react/dist/ssr';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';

import { PLAYBACK_SPEEDS } from '../utils/podcastPlaybackStorage';
import { useIsClient } from '../utils/useIsClient';

import * as styles from './AudioPlayer.css';

type Props = {
  value: number;
  onChange: (speed: number) => void;
  color?: string;
};

function formatSpeed(speed: number): string {
  return `${speed}×`;
}

export function PlaybackSpeedControl({ value, onChange, color }: Props) {
  const isClient = useIsClient();
  const closedByPointer = useRef(false);
  const label = formatSpeed(value);

  if (!isClient) {
    return (
      <button type="button" className={styles.speedButton} style={{ color }} aria-label="Playback speed">
        <span className={styles.speedLabel}>{label}</span>
      </button>
    );
  }

  return (
    <DropdownMenuPrimitive.Root>
      <DropdownMenuPrimitive.Trigger
        className={styles.speedButton}
        style={{ color }}
        aria-label="Playback speed"
        onPointerDown={() => {
          closedByPointer.current = true;
        }}
      >
        <span className={styles.speedLabel}>{label}</span>
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Content
        className={styles.speedMenu}
        side="top"
        sideOffset={6}
        align="end"
        onPointerDownCapture={() => {
          closedByPointer.current = true;
        }}
        onKeyDownCapture={() => {
          closedByPointer.current = false;
        }}
        onCloseAutoFocus={(event) => {
          if (closedByPointer.current) {
            event.preventDefault();
          }
        }}
      >
        <DropdownMenuPrimitive.RadioGroup value={String(value)} onValueChange={(speed) => onChange(Number(speed))}>
          {PLAYBACK_SPEEDS.map((speed) => (
            <DropdownMenuPrimitive.RadioItem key={speed} className={styles.speedMenuItem} value={String(speed)}>
              {formatSpeed(speed)}
              <span className={styles.speedMenuIndicator}>
                <DropdownMenuPrimitive.ItemIndicator>
                  <Check size={14} weight="bold" />
                </DropdownMenuPrimitive.ItemIndicator>
              </span>
            </DropdownMenuPrimitive.RadioItem>
          ))}
        </DropdownMenuPrimitive.RadioGroup>
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Root>
  );
}
